import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { productRows, quoteKey, routeSummary, selectedQuote } from "../booking.ts";
import type { Place } from "../page-location.ts";
import type { RideProduct, RideQuote, RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { MapHero } from "./Hero.tsx";
import { Absent, PhoneShell } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `2 Choose` — one row per product class, with a price against each.
 *
 * Three services, and none of them is EXP-002. ADR 0026 section 1 considered this screen for the BFF
 * and **rejected** it: "the only way to get a price is `POST /v1/ride-quotes`, which mints an
 * immutable priced resource. A BFF issuing three of them to draw one screen would be deciding how
 * many quotes a tenant holds, which is a write and a domain decision at once." So the application
 * does it, and `booking.ts` carries the full reasoning about what that costs and what bounds it.
 *
 * | Drawn                              | From                                        |
 * | ---------------------------------- | ------------------------------------------- |
 * | `24 km · about 38 min`             | GEO-04 `POST /v1/distance-eta`              |
 * | `Standard` / `XL` / `Assist` rows  | MOB-01 `GET /v1/ride-products`              |
 * | `KES 1,450` per row                | MOB-02 `POST /v1/ride-quotes`, one per row  |
 * | `3 min` per row                    | **nothing** — the measured absence          |
 *
 * ## The glyphs are not the design's, and cannot be
 *
 * The design hard-codes `local_taxi`, `airport_shuttle` and `accessible` against its three sample
 * products. MOB-01 publishes `vehicleClass` as a free string, `displayName` as prose and
 * `supportedRiderCapabilities` as capability names — nothing that says which glyph a product has. So
 * the glyph comes from `travelMode`, the one enumerated field about what a product *is*, using
 * `CLAUDE.md` section 3.5's own vocabulary. A tenant whose catalogue is three car products draws
 * three `local_taxi` rows where the design draws three different glyphs, which is a real difference
 * and is reported rather than closed by matching on a display name.
 */

export interface ChooseRideProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly customerId: string;
  readonly origin: Place | undefined;
  readonly destination: Place | undefined;
  /** What `1 Where to` called the destination, so the chip can name the route. */
  readonly routeLabel: string | undefined;
  readonly onQuoteChosen: (quoteId: string) => void;
  readonly onBack: () => void;
}

export function ChooseRide(props: ChooseRideProps): ReactNode {
  const { api, origin, destination, customerId } = props;

  const [products, setProducts] = useState<readonly RideProduct[]>([]);
  const [quotes, setQuotes] = useState<ReadonlyMap<string, RideQuote>>(new Map());
  const [failed, setFailed] = useState<ReadonlySet<string>>(new Set());
  const [route, setRoute] = useState<{ distanceMetres: number; durationSeconds: number } | null>(
    null,
  );
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    if (origin === undefined || destination === undefined) return;
    setState({ kind: "loading" });
    api
      .rideProducts()
      .then((answer) => {
        setProducts(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.products])));
  }, [api, origin, destination]);

  useEffect(load, [load]);

  /*
   * The route, from GEO-04, in the travel mode of the product a rider is looking at.
   *
   * `car` when nothing is selected yet, because the design draws the route summary before any class
   * is chosen and GEO-04 requires a mode. That default is the application's own and is visible: a
   * tenant whose catalogue is motorcycles only would see a car's route until a row was tapped. It is
   * recorded rather than hidden, and the alternative — no summary until a class is chosen — loses a
   * designed element that is drawn above the rows.
   */
  const mode = useMemo(
    () => products.find((product) => product.productId === selected)?.travelMode ?? "car",
    [products, selected],
  );

  useEffect(() => {
    if (origin === undefined || destination === undefined) return;
    let current = true;
    api
      .distanceEta({ origin, destination, mode })
      .then((answer) => {
        if (current) {
          setRoute({
            distanceMetres: answer.result.distanceMetres,
            durationSeconds: answer.result.durationSeconds,
          });
        }
      })
      .catch(() => {
        if (current) setRoute(null);
      });
    return (): void => {
      current = false;
    };
  }, [api, origin, destination, mode]);

  /*
   * One quote per active product, under a key derived from the route and the product.
   *
   * The key is the whole safety of this: MOB-02 requires an `Idempotency-Key` and a random one per
   * render would mint a fresh immutable priced resource every time the screen was drawn. Derived, the
   * same screen asks for the same quote however many times it is rendered, and Document 06 refuses a
   * key reused with materially different terms rather than answering from the first request — so a
   * collision is a visible refusal rather than somebody else's price.
   */
  useEffect(() => {
    if (origin === undefined || destination === undefined) return;
    if (!props.scopes.has(SCOPES.quote)) return;
    let current = true;
    for (const product of products) {
      if (product.lifecycle !== "active") continue;
      const key = quoteKey({ customerId, productId: product.productId, origin, destination });
      api
        .createQuote({ productId: product.productId, origin, destination }, key)
        .then((quote) => {
          if (!current) return;
          setQuotes((held) => new Map(held).set(product.productId, quote));
        })
        .catch(() => {
          if (!current) return;
          setFailed((held) => new Set(held).add(product.productId));
        });
    }
    return (): void => {
      current = false;
    };
  }, [api, products, origin, destination, customerId, props.scopes]);

  const rows = useMemo(
    () => productRows({ products, quotes, failed, selectedProductId: selected }),
    [products, quotes, failed, selected],
  );

  const chosen = selectedQuote(quotes, selected);
  const chosenName = products.find((product) => product.productId === selected)?.displayName;

  if (origin === undefined || destination === undefined) {
    /*
     * Reached by a shared or stale link that names this screen without a route.
     *
     * Nothing on this platform holds "the route a rider is looking at" before a quote exists, so
     * there is nothing to look up and nothing to guess. The screen says that rather than redirecting,
     * because a rider sent back to `1 Where to` with no explanation would conclude the link was
     * wrong rather than that it was incomplete.
     */
    return (
      <StateScreen
        screen="choose"
        chip={{ class: "unknown", label: "no route" }}
        title="We don't know where you're going"
        sub="This link names a ride without the two points it is between, and nothing published remembers a route before a price is made. Start again from Where to."
      />
    );
  }

  if (state.kind !== "ready") {
    return (
      <StateScreen
        screen="choose"
        state={state}
        onRetry={load}
        purpose="Looking up the classes you can ride in."
      />
    );
  }

  return (
    <PhoneShell
      screen="choose"
      hero={<MapHero kind="route" />}
      back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      safety={{ armed: false }}
      chip={{
        class: "active",
        label: props.routeLabel ?? "Your route",
      }}
      title="Choose a ride"
      /*
       * `24 km · about 38 min`, from GEO-04's own two numbers.
       *
       * An absence when GEO-04 did not answer, rather than a blank: the sub-line is the one place on
       * this screen that says how far the ride is, and a rider comparing three prices with no
       * distance is comparing three numbers.
       */
      sub={
        route === null
          ? "We couldn't measure this route"
          : routeSummary(route.distanceMetres, route.durationSeconds)
      }
      actions={[
        {
          label: chosenName === undefined ? "Continue" : `Continue with ${chosenName}`,
          glyph: "arrow_forward",
          primary: true,
          ...(chosen === undefined
            ? {
                /*
                 * Drawn and unpressable until there is a quote to carry forward.
                 *
                 * Two different reasons, said apart: a rider who has chosen nothing is told to
                 * choose, and a rider whose chosen class came back without a price is told that —
                 * because the second is a refusal by MOB-02 and the first is a step not taken, and
                 * only one of them is worth retrying.
                 */
                unavailable:
                  selected === undefined
                    ? "Choose a class first."
                    : "No price came back for that class, so there is nothing to confirm.",
              }
            : { onPress: (): void => props.onQuoteChosen(chosen.id) }),
        },
      ]}
    >
      {rows.length === 0 ? (
        <Absent words="No ride classes are published for this tenant" />
      ) : (
        <div className="options" role="radiogroup" aria-label="Ride classes">
          {rows.map((row) => (
            <button
              key={row.productId}
              type="button"
              role="radio"
              aria-checked={row.selected}
              className={`option${row.selected ? " option--selected" : ""}`}
              onClick={() => setSelected(row.productId)}
            >
              <span className="glyph option__glyph" aria-hidden="true">
                {row.glyph}
              </span>
              <span className="option__text">
                <span className="option__name">{row.name}</span>
                <span className="option__attributes">{row.attributes}</span>
              </span>
              <span className="option__right">
                {row.price === null ? (
                  <Absent words={row.priceBecause} className="option__price" />
                ) : (
                  <span className="option__price">{row.price}</span>
                )}
                {/*
                 * `3 min` — how long until a driver of that class could reach the rider.
                 *
                 * The one measured absence on this screen. Nothing publishes
                 * `result.pickupEtaSeconds`, `products.pickupEtaSeconds` or `availability.etaSeconds`,
                 * and GEO-04 answers the distance between two points rather than the distance to the
                 * nearest free car of a class. A number here would be a rider choosing which class to
                 * pay for on a figure nobody computed.
                 */}
                <Absent words="no pickup eta" className="option__eta" />
              </span>
            </button>
          ))}
        </div>
      )}
    </PhoneShell>
  );
}
